import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * The phone app bar (`header.mbar`, P/Member_Hub.html:498-507, 863-871): the AGU mark — or a back chevron when the view has somewhere
 * to go back to — the screen title, the search icon and the member's initials, which open the drawer.
 *
 * A new ARIA implementation rather than MJ chrome: MJ ships no app-bar primitive, and the off-canvas `mj-left-nav` it pairs with cannot
 * take the RA token layer (docs/mj-upstream-defects.md MJ-G10).
 */
@Component({
    standalone: false,
    selector: 'agu-app-bar',
    templateUrl: './app-bar.component.html',
    styleUrl: './app-bar.component.scss',
})
export class AGUAppBarComponent {
    /** The screen title (`.mtitle`): the app name, or on a detail view the label of where Back goes. */
    @Input({ required: true }) Title!: string;
    /**
     * Set on a view with a parent to return to: the label of that parent ("Grant Finder"). The lead becomes a back chevron named
     * `BackAriaLabel`. Null shows the mark.
     */
    @Input() BackLabel: string | null = null;
    /** Whether the app has a Search screen for the icon to open. */
    @Input() ShowSearch = false;
    /** The member's initials, on the avatar that opens the drawer. */
    @Input() Initials = '';
    /** Whether the drawer the avatar controls is open, for its `aria-expanded` (P/Member_Hub.html:870, 891). */
    @Input() MenuOpen = false;

    /** The AGU mark is the home link (`.mlead` → Member Hub home, P/Member_Hub.html:866). */
    @Output() HomeRequested = new EventEmitter<void>();
    @Output() BackRequested = new EventEmitter<void>();
    @Output() SearchRequested = new EventEmitter<void>();
    @Output() AvatarRequested = new EventEmitter<void>();

    /**
     * The chevron's accessible name, "Back to {BackLabel}" — the crumb's own rule: the prototype strips "Back to " for display and
     * restores it in `aria-label` (P/detail.html:1080-1083).
     */
    get BackAriaLabel(): string {
        return `Back to ${this.BackLabel ?? ''}`;
    }

    RequestHome(): void { this.HomeRequested.emit(); }
    RequestBack(): void { this.BackRequested.emit(); }
    RequestSearch(): void { this.SearchRequested.emit(); }
    RequestAvatar(): void { this.AvatarRequested.emit(); }
}
